import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Typography, alpha, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import SurveyHeader from './SurveyHeader';
import SurveyCard from './SurveyCard';
import SurveyForm from './SurveyForm';
import SurveyPreview from './SurveyPreview';
import SurveyResponses from './SurveyResponses';
import { RiSurveyLine } from 'react-icons/ri';

// Color palette
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  error: '#f44336',
  purple: '#673ab7',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
};

const SurveysContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: COLORS.background,
  minHeight: '100vh',
}));

const EmptyStateContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  border: `1px solid ${COLORS.lightBorder}`,
}));

export default function Surveys({
  profile,
  surveys,
  surveysLoading,
  creatingSurvey,
  updatingSurvey,
  deletingSurvey,
  togglingSurvey,
  surveysErrorMessage,
  sucursal_id,
  responses,
  responsesLoading,
  responsesErrorMessage,
  getSurveys,
  createSurvey,
  updateSurvey,
  deleteSurvey,
  getSurveyResponses,
}) {
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [openResponsesDialog, setOpenResponsesDialog] = useState(false);

  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    question: '',
    type: 'rating',
    max_rating: 5,
  });

  useEffect(() => {
    getSurveys(profile.tenant_id, sucursal_id);
    
    const interval = setInterval(() => {
      getSurveys(profile.tenant_id, sucursal_id);
    }, 30000); 
    
    return () => clearInterval(interval);
  }, [getSurveys, profile, sucursal_id]);

  const handleCreateSurvey = () => {
    setEditingSurvey(null);
    setFormData({
      title: '',
      question: '',
      type: 'rating',
      max_rating: 5,
    });
    setOpenFormDialog(true);
  };

  const handleEditSurvey = survey => {
    setEditingSurvey(survey);
    setFormData({
      title: survey.title,
      question: survey.question,
      type: survey.type,
      max_rating: survey.max_rating || 5,
    });
    setOpenFormDialog(true);
  };

  const handlePreviewSurvey = survey => {
    setPreviewSurvey(survey);
    setOpenPreviewDialog(true);
  };

  const handleViewResponses = survey => {
    setSelectedSurvey(survey);
    setOpenResponsesDialog(true);
    getSurveyResponses(survey.id);
  };

  const handleSaveSurvey = () => {
    if (editingSurvey) {
      // Update existing survey
      updateSurvey(editingSurvey.id, {
        ...formData,
        status: editingSurvey.status,
        tenant_id: profile.tenant_id,
        sucursal_id: sucursal_id,
      }).then(result => {
        if (result.success) {
          setOpenFormDialog(false);
        }
      });
    } else {
      // Create new survey
      createSurvey({
        ...formData,
        status: 'inactive',
        tenant_id: profile.tenant_id,
        sucursal_id: sucursal_id,
      }).then(result => {
        if (result.success) {
          setOpenFormDialog(false);
        }
      });
    }
  };

  const handleDeleteSurvey = surveyId => {
    if (deletingId || togglingId) {
      return;
    }
    
    setDeletingId(surveyId);
    deleteSurvey(surveyId, profile.tenant_id, sucursal_id).then(result => {
      setDeletingId(null);
    }).catch(error => {
      console.error('Error deleting survey:', error);
      setDeletingId(null);
    });
  };

  const handleToggleStatus = surveyId => {
    if (deletingId || togglingId) {
      return;
    }

    const targetSurvey = surveys.find(s => s.id === surveyId);
    if (!targetSurvey) return;

    const newStatus = targetSurvey.status === 'active' ? 'paused' : 'active';

    setTogglingId(surveyId);

    updateSurvey(surveyId, {
      ...targetSurvey,
      status: newStatus,
      tenant_id: profile.tenant_id,
      sucursal_id: sucursal_id,
    }).then(result => {
      if (result.success) {
        getSurveys(profile.tenant_id, sucursal_id);
      }
      setTogglingId(null);
    }).catch(error => {
      console.error('Error toggling survey status:', error);
      setTogglingId(null);
    });
  };

  const hasActiveSurvey = () => {
    return surveys && surveys.length > 0 && surveys.some(survey => survey.status === 'active');
  };

  const getActiveSurveyCount = () => {
    return surveys.filter(survey => survey.status === 'active').length;
  };

  const renderEmptyState = () => (
    <EmptyStateContainer>
      <RiSurveyLine size={64} color={alpha(COLORS.primary, 0.5)} style={{ marginBottom: 16 }} />
      <Typography variant="h5" fontWeight={600} color="text.primary" gutterBottom>
        No hay encuestas creadas
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
        Crea tu primera encuesta de satisfacción para comenzar a medir la experiencia de tus clientes con el chatbot.
      </Typography>
    </EmptyStateContainer>
  );

  const renderContent = () => {
    if (surveysLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}>
          <CircularProgress />
        </Box>
      );
    }

    if (surveys.length === 0) {
      return renderEmptyState();
    }

    return (
      <>
        {surveys.length > 0 && getActiveSurveyCount() === 0 && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: alpha(COLORS.warning, 0.1),
              border: `1px solid ${alpha(COLORS.warning, 0.3)}`,
              borderRadius: 1,
              textAlign: 'center',
            }}>
            <Typography variant="body2" color="text.secondary">
              <strong>💡 Tip:</strong> Tienes {surveys.length} encuesta
              {surveys.length > 1 ? 's' : ''} creada
              {surveys.length > 1 ? 's' : ''}, pero ninguna está activa. Activa
              una encuesta para comenzar a recibir respuestas de tus clientes.
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {surveys.map(survey => (
            <Grid item xs={12} sm={6} md={4} key={survey.id}>
              <SurveyCard
                survey={survey}
                onEdit={handleEditSurvey}
                onDelete={handleDeleteSurvey}
                onToggleStatus={handleToggleStatus}
                onPreview={handlePreviewSurvey}
                onViewResponses={handleViewResponses}
                deleting={deletingId === survey.id}
                toggling={togglingId === survey.id}
                disabled={deletingId !== null || togglingId !== null}
              />
            </Grid>
          ))}
        </Grid>
      </>
    );
  };

  return (
    <SurveysContainer>
      <SurveyHeader
        onCreateSurvey={handleCreateSurvey}
        hasActiveSurvey={hasActiveSurvey()}
        loading={surveysLoading}
      />

      {renderContent()}

      {/* Survey Form Dialog */}
      <SurveyForm
        open={openFormDialog}
        onClose={() => setOpenFormDialog(false)}
        onSave={handleSaveSurvey}
        editingSurvey={editingSurvey}
        formData={formData}
        setFormData={setFormData}
        loading={creatingSurvey || updatingSurvey}
      />

      {/* Survey Preview Dialog */}
      <SurveyPreview
        open={openPreviewDialog}
        onClose={() => setOpenPreviewDialog(false)}
        survey={previewSurvey}
      />

      {/* Survey Responses Dialog */}
      <SurveyResponses
        open={openResponsesDialog}
        onClose={() => setOpenResponsesDialog(false)}
        survey={selectedSurvey}
        responses={responses}
        loading={responsesLoading}
        errorMessage={responsesErrorMessage}
        getSurveyResponses={getSurveyResponses}
      />
    </SurveysContainer>
  );
}
